// src/components/LoanScheduleManager.jsx
import React, { useState, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_PATH}`;

const LoanScheduleManager = () => {
    const [groupId, setGroupId] = useState('');
    const [shiftDirection, setShiftDirection] = useState('next');
    const [targetWeekday, setTargetWeekday] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const toastRef = useRef(null);

    const weekdays = [
        { label: 'No adjustment', value: null },
        { label: 'Monday', value: 'Monday' },
        { label: 'Tuesday', value: 'Tuesday' },
        { label: 'Wednesday', value: 'Wednesday' },
        { label: 'Thursday', value: 'Thursday' },
        { label: 'Friday', value: 'Friday' },
        { label: 'Saturday', value: 'Saturday' },
        { label: 'Sunday', value: 'Sunday' }
    ];

    const showToast = useCallback((severity, summary, detail) => {
        if (toastRef.current) {
            toastRef.current.show({ severity, summary, detail, life: 5000 });
        }
    }, []);

    const handlePreview = useCallback(async () => {
        if (!groupId || groupId.trim() === '') {
            showToast('warn', 'Validation Error', 'Please enter Group ID');
            return;
        }

        setLoading(true);
        
        try {
            const response = await axios.post(`${API_BASE_URL}/loanschedule/preview`, {
                groupId: groupId,
                shiftDirection: shiftDirection,
                targetWeekday: targetWeekday
            });
            
            if (response.data.success) {
                setPreviewData(response.data.previewData);
                showToast('success', 'Preview Ready', `${response.data.previewData?.length || 0} schedules found`);
            } else {
                showToast('error', 'Preview Failed', response.data.message);
                setPreviewData(null);
            }
        } catch (err) {
            showToast('error', 'Error', err.response?.data?.message || 'Error fetching preview');
            setPreviewData(null);
        } finally {
            setLoading(false);
        }
    }, [groupId, shiftDirection, targetWeekday, showToast]);

    const handleUpdate = useCallback(async () => {
        setLoading(true);
        
        try {
            const response = await axios.post(`${API_BASE_URL}/loanschedule/update`, {
                groupId: groupId,
                shiftDirection: shiftDirection,
                targetWeekday: targetWeekday
            });
            
            if (response.data.success) {
                showToast('success', 'Update Successful', response.data.message);
                setShowConfirmDialog(false);
                // Refresh preview after update
                await handlePreview();
            } else {
                showToast('error', 'Update Failed', response.data.message);
                if (response.data.warnings) {
                    response.data.warnings.forEach(warning => {
                        showToast('warn', 'Warning', warning);
                    });
                }
            }
        } catch (err) {
            showToast('error', 'Error', err.response?.data?.message || 'Error updating schedules');
        } finally {
            setLoading(false);
        }
    }, [groupId, shiftDirection, targetWeekday, handlePreview, showToast]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    }, []);

    const getDateDifference = useCallback((oldDate, newDate) => {
        if (!oldDate || !newDate) return 0;
        const diff = Math.ceil((new Date(newDate) - new Date(oldDate)) / (1000 * 60 * 60 * 24));
        return diff;
    }, []);

    const getChangeStatus = useCallback((oldDate, newDate) => {
        const diff = getDateDifference(oldDate, newDate);
        if (diff > 0) return 'positive';
        if (diff < 0) return 'negative';
        return 'neutral';
    }, [getDateDifference]);

    const dateBodyTemplate = useCallback((rowData) => {
        return formatDate(rowData.oldPayDate);
    }, [formatDate]);

    const newDateBodyTemplate = useCallback((rowData) => {
        const status = getChangeStatus(rowData.oldPayDate, rowData.newPayDate);
        const diff = getDateDifference(rowData.oldPayDate, rowData.newPayDate);
        
        return (
            <div className="flex align-items-center gap-2">
                <span>{formatDate(rowData.newPayDate)}</span>
                {status !== 'neutral' && (
                    <Tag 
                        value={`${diff > 0 ? '+' : ''}${diff} days`}
                        severity={status === 'positive' ? 'warning' : 'info'}
                        className="ml-2"
                    />
                )}
            </div>
        );
    }, [formatDate, getChangeStatus, getDateDifference]);

    const paymentBodyTemplate = useCallback((rowData) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(rowData.payment);
    }, []);

    const balanceBodyTemplate = useCallback((rowData) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(rowData.currentBalance);
    }, []);

    const confirmDialogFooter = (
        <div>
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                onClick={() => setShowConfirmDialog(false)} 
                className="p-button-text" 
            />
            <Button 
                label="Confirm Update" 
                icon="pi pi-check" 
                onClick={handleUpdate} 
                autoFocus 
            />
        </div>
    );

    const header = (
        <div className="flex align-items-center justify-content-between">
            <div className="flex align-items-center">
                <i className="pi pi-calendar mr-2" style={{ fontSize: '1.5rem' }}></i>
                <span className="text-xl font-bold">Loan Schedule Date Manager</span>
            </div>
        </div>
    );

    return (
        <div className="p-4">
            <Toast ref={toastRef} />
            
            <Card header={header} className="shadow-2">
                <div className="p-fluid">
                    <div className="grid">
                        {/* Group ID Input */}
                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label htmlFor="groupId" className="font-semibold">Group ID</label>
                                <InputText
                                    id="groupId"
                                    value={groupId}
                                    onChange={(e) => setGroupId(e.target.value)}
                                    placeholder="Enter Group ID (e.g., GRP001, 123, ABC)"
                                    className="w-full"
                                />
                                <small className="block text-muted mt-1">
                                    Enter alphanumeric Group ID
                                </small>
                            </div>
                        </div>

                        {/* Shift Direction */}
                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="font-semibold mb-2 block">Shift Direction</label>
                                <div className="flex gap-3">
                                    <div className="field-radiobutton">
                                        <RadioButton
                                            inputId="next"
                                            name="shiftDirection"
                                            value="next"
                                            onChange={(e) => setShiftDirection(e.value)}
                                            checked={shiftDirection === 'next'}
                                        />
                                        <label htmlFor="next" className="ml-2">Next Day (+1)</label>
                                    </div>
                                    <div className="field-radiobutton">
                                        <RadioButton
                                            inputId="previous"
                                            name="shiftDirection"
                                            value="previous"
                                            onChange={(e) => setShiftDirection(e.value)}
                                            checked={shiftDirection === 'previous'}
                                        />
                                        <label htmlFor="previous" className="ml-2">Previous Day (-1)</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Target Weekday */}
                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label htmlFor="weekday" className="font-semibold">Target Weekday (Optional)</label>
                                <Dropdown
                                    id="weekday"
                                    value={targetWeekday}
                                    options={weekdays}
                                    onChange={(e) => setTargetWeekday(e.value)}
                                    placeholder="Select a weekday"
                                    className="w-full"
                                />
                                <small className="block text-muted mt-1">
                                    Adjust shifted dates to selected weekday
                                </small>
                            </div>
                        </div>

                        {/* Preview Button */}
                        <div className="col-12 md:col-6 lg:col-3 flex align-items-end">
                            <Button
                                label="Preview Changes"
                                icon="pi pi-eye"
                                onClick={handlePreview}
                                loading={loading}
                                className="w-full mb-3"
                                severity="primary"
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Preview Data Table */}
            {previewData && previewData.length > 0 && (
                <Card className="mt-4 shadow-2">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <div>
                            <i className="pi pi-table mr-2"></i>
                            <span className="text-lg font-semibold">Schedule Preview</span>
                            <Tag value={`${previewData.length} schedules`} className="ml-2" severity="info" />
                        </div>
                        <Button
                            label="Apply Changes"
                            icon="pi pi-save"
                            onClick={() => setShowConfirmDialog(true)}
                            severity="success"
                        />
                    </div>

                    <DataTable 
                        value={previewData} 
                        scrollable 
                        scrollHeight="400px"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        className="p-datatable-sm"
                        emptyMessage="No schedules found"
                    >
                        <Column field="loanAccountNo" header="Loan Account" sortable style={{ minWidth: '150px' }} />
                        <Column field="period" header="Period" sortable style={{ minWidth: '80px' }} />
                        <Column field="oldPayDate" header="Current Date" body={dateBodyTemplate} sortable style={{ minWidth: '120px' }} />
                        <Column field="newPayDate" header="New Date" body={newDateBodyTemplate} sortable style={{ minWidth: '200px' }} />
                        <Column field="payment" header="Payment" body={paymentBodyTemplate} sortable style={{ minWidth: '120px' }} />
                        <Column field="currentBalance" header="Balance" body={balanceBodyTemplate} sortable style={{ minWidth: '120px' }} />
                    </DataTable>
                </Card>
            )}

            {/* Preview Empty State */}
            {previewData && previewData.length === 0 && (
                <Card className="mt-4 shadow-2">
                    <div className="text-center p-4">
                        <i className="pi pi-inbox text-4xl text-400 mb-2"></i>
                        <p className="text-600">No schedules found for this group</p>
                    </div>
                </Card>
            )}

            {/* Confirm Dialog */}
            <Dialog
                header="Confirm Schedule Update"
                visible={showConfirmDialog}
                style={{ width: '450px' }}
                footer={confirmDialogFooter}
                onHide={() => setShowConfirmDialog(false)}
                draggable={false}
                resizable={false}
            >
                <div className="p-3">
                    <div className="flex align-items-center mb-3">
                        <i className="pi pi-exclamation-triangle text-2xl text-warning mr-2"></i>
                        <span className="text-lg font-semibold">Are you sure?</span>
                    </div>
                    
                    <div className="mb-3">
                        <div className="flex justify-content-between mb-2">
                            <span className="font-semibold">Group ID:</span>
                            <span>{groupId}</span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="font-semibold">Action:</span>
                            <span>Shift {shiftDirection === 'next' ? 'forward' : 'backward'} by 1 day</span>
                        </div>
                        {targetWeekday && (
                            <div className="flex justify-content-between mb-2">
                                <span className="font-semibold">Weekday Adjustment:</span>
                                <span>Adjust to {targetWeekday}</span>
                            </div>
                        )}
                        <div className="flex justify-content-between">
                            <span className="font-semibold">Schedules affected:</span>
                            <span className="text-primary font-bold">{previewData?.length || 0}</span>
                        </div>
                    </div>

                    <Message 
                        severity="warn" 
                        text="This action cannot be undone. Please review the preview carefully before confirming." 
                        className="w-full"
                    />
                </div>
            </Dialog>

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed top-0 left-0 w-full h-full flex align-items-center justify-content-center bg-white-alpha-90 z-5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                    <div className="text-center">
                        <ProgressSpinner />
                        <div className="mt-3">Processing...</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoanScheduleManager;